import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { AnimatedSpinner } from '../../../components/AnimatedSpinner'

export interface VideoUploaderProps {
  value: string
  onChange?: (url: string, file: File) => void
  error?: string
  loading?: boolean
  id?: string
  disabled?: boolean
}

export function VideoUploader({
  value,
  onChange,
  loading,
  error,
  id,
  disabled = false,
}: VideoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isError, setIsError] = useState(error)
  const [isLoading, setIsLoading] = useState(!!value)

  useEffect(() => {
    if (error) {
      setIsError(error)
    } else {
      setIsError('')
    }
  }, [error, value])

  useEffect(() => {
    if (loading !== undefined) {
      setIsLoading(loading)
    }
  }, [loading])

  const handleError = () => {
    setIsLoading(false)
    setIsError('Failed to load video')
  }

  const handleFileChange = (file?: File) => {
    if (file && file.type === 'video/mp4') {
      const maxSizeInBytes = 20 * 1024 * 1024 // 20 MB
      if (file.size > maxSizeInBytes) {
        setIsError('Video file size must be less than 20 MB')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          onChange?.(reader.result, file)
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    handleFileChange(file)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    handleFileChange(file)
  }

  return (
    <div
      id={id}
      onDrop={handleDrop}
      onDragOver={e => e.preventDefault()}
      className={clsx(
        'border-dashed border-2 border-gray-300 rounded-2xl w-full p-2 flex flex-col items-center justify-center cursor-pointer min-h-48 group relative',
        { 'pointer-events-none': isLoading || disabled },
      )}
      onClick={() => inputRef.current?.click()}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <AnimatedSpinner />
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      {value && (
        <video
          src={value}
          className="max-h-40 rounded"
          onError={handleError}
          onLoadedData={() => {
            setIsLoading(false)
          }}
          autoPlay
          loop
          muted
          playsInline
        />
      )}

      <div
        className={clsx({
          'absolute w-full h-full rounded-2xl p-4 hidden bg-black text-white backdrop-blur-sm group-hover:flex group-hover:flex-col group-hover:justify-center group-hover:items-center group-hover:bg-opacity-30 transition duration-300':
            value,
          'flex flex-col justify-center items-center': !value && !isLoading,
          hidden: isLoading || disabled,
        })}
      >
        <i className="fas fa-upload text-3xl mb-2" aria-hidden="true"></i>
        <span className="text-center">
          Drag & drop or click to upload video
        </span>
      </div>

      {isError && <span className="text-red-500 text-xs mt-2">{isError}</span>}
    </div>
  )
}
